import {
  Col,
  DropdownMenu,
  Icon,
  type IconName,
  type MenuItem,
  Tappable,
  Text,
  useTheme,
  useTransition,
  type ViewHandle,
} from "@rally/ui-kit";
import { useRef, useState } from "react";
import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import type { PutAway, TargetCapabilities } from "../net/api.ts";
import { act } from "../net/rally.ts";
import { useCoarsePointer } from "./panel/pointer.ts";
import { Reacted } from "./reaction/reacted.tsx";
import type { Reaction, Target } from "./reaction/reacting.ts";
import { snoozeOffers, whenItMeans } from "./snooze.ts";
import { actionPresentation, actionsFor, undoScope } from "./target-actions.ts";

function whereItIs(
  node: ViewHandle | null,
  then: (anchor: { x: number; y: number; width: number; height: number }) => void,
) {
  node?.measureInWindow((x, y, width, height) => then({ x, y, width, height }));
}

function Face({
  icon,
  label,
  active = false,
  strong = false,
  compact = false,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  strong?: boolean;
  compact?: boolean;
}) {
  const theme = useTheme();
  return (
    <Col
      style={{
        height: compact ? 28 : 26,
        width: compact ? 28 : undefined,
        justifyContent: "center",
        paddingHorizontal: compact ? 0 : theme.space[2],
        borderRadius: theme.radius.round,
        borderWidth: compact ? 0 : 1,
        borderColor: active
          ? theme.color.accent
          : strong
            ? theme.color["border.strong"]
            : theme.color["border.default"],
        backgroundColor: active
          ? theme.color.accentSoft
          : strong
            ? theme.color["bg.surfaceAlt"]
            : "transparent",
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
      }}
    >
      <Icon
        name={icon}
        size={compact ? 15 : 13}
        color={active ? "accent" : strong ? "text.primary" : "text.tertiary"}
      />
      {compact ? null : (
        <Text typeName="caption" color={active ? "accent" : strong ? "text.secondary" : "text.tertiary"}>
          {label}
        </Text>
      )}
    </Col>
  );
}

function Action({
  icon,
  label,
  onPress,
  active,
  testID,
  onAnchor,
  disabled = false,
  compact = false,
}: {
  icon: IconName;
  label: string;
  onPress?: () => void;
  active?: boolean;
  testID: string;
  onAnchor?: (anchor: { x: number; y: number; width: number; height: number }) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const box = useRef<ViewHandle | null>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <Tappable
      ref={box}
      onPress={() => (onAnchor ? whereItIs(box.current, onAnchor) : onPress?.())}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: active, disabled }}
      testID={testID}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{ opacity: disabled ? 0.45 : 1 }}
    >
      <Face icon={icon} label={label} active={active} strong={hovered || focused} compact={compact} />
    </Tappable>
  );
}

function MenuAction({
  items,
  icon,
  label,
  testID,
  compact = false,
}: {
  items: MenuItem[];
  icon: IconName;
  label: string;
  testID: string;
  compact?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <Col
      {...({
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
      } as object)}
    >
      <DropdownMenu
        items={items}
        width={220}
        trigger={
          <Col testID={testID} style={{ pointerEvents: "none" }}>
            <Face icon={icon} label={label} strong={hovered || focused} compact={compact} />
          </Col>
        }
      />
    </Col>
  );
}

export function TargetActions({
  target,
  reactions,
  away,
  capabilities,
  onReply,
  onChanged,
  onError,
  phone = false,
  reaction = true,
  testID = "target-actions",
  revealed = false,
}: {
  target: Target;
  reactions: readonly Reaction[];
  away: PutAway;
  capabilities: TargetCapabilities;
  onReply?: () => void;
  onChanged?: () => void;
  onError?: (cause: unknown) => void;
  phone?: boolean;

  reaction?: boolean;
  testID?: string;

  revealed?: boolean;
}) {
  const theme = useTheme();
  const fade = useTransition("opacity", "fast");
  const coarse = useCoarsePointer();
  const presentation = actionPresentation({
    coarse,
    rowHovered: revealed,
    rowFocused: revealed,
    chipHovered: false,
    chipFocused: false,
  });
  const floats = !coarse;
  const [failure, setFailure] = useState<string | null>(null);
  const [picking, setPicking] = useState<{ x: number; y: number; width: number; height: number } | null>(
    null,
  );
  const pinScope = undoScope(away.pinnedForMe, away.pinnedForAll);
  const archiveScope = undoScope(away.archivedForMe, away.archivedForAll);
  const snoozed = away.snoozedUntil !== null;
  const failed = (cause: unknown) => {
    setFailure(failureSaid(cause, say("chat.broke")));
    onError?.(cause);
  };
  const perform = (name: string, body: object) => {
    setFailure(null);
    void act(name, body)
      .then(() => onChanged?.())
      .catch(failed);
  };
  const snoozeRows: MenuItem[] = snoozeOffers(new Date()).map((offer) => ({
    label: offer.label,
    hint: whenItMeans(offer.until, new Date()),
    onPress: () => perform("attention.snooze", { target, until: offer.until }),
  }));

  return (
    <Col
      style={
        {
          gap: theme.space[1],
          overflow: "visible",
          opacity: floats ? 1 : presentation.rowOpacity,
          ...fade,
        } as object
      }
      testID={testID}
    >
      {reaction && capabilities.react ? (
        <Reacted
          target={target}
          reactions={reactions}
          hovered={false}
          phone={phone}
          picking={picking}
          onPicking={setPicking}
          onChanged={onChanged}
          onError={failed}
          add={false}
          testID={`${testID}-reactions`}
        />
      ) : null}
      <Col
        style={
          (floats
            ? {
                position: "absolute",
                right: 0,
                bottom: -10,
                zIndex: 2,
                flexDirection: "row",
                alignItems: "center",
                gap: theme.space[1],
                padding: 3,
                borderRadius: theme.radius.round,
                borderWidth: 1,
                borderColor: theme.color["border.default"],
                backgroundColor: theme.color["bg.surface"],
                opacity: revealed ? 1 : 0,
                pointerEvents: revealed ? "auto" : "none",
                ...fade,
                ...theme.elevation.raised,
              }
            : {
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: theme.space[1],
              }) as object
        }
      >
        {actionsFor(Boolean(onReply), reaction)
          .filter((action) => capabilities[action])
          .map((action) => {
            if (action === "react") {
              return (
                <Action
                  key={action}
                  icon="smile"
                  label={say("actions.react")}
                  testID={`${testID}-react`}
                  compact={floats}
                  onAnchor={(at) => setPicking((was) => (was ? null : at))}
                />
              );
            }
            if (action === "reply") {
              return (
                <Action
                  key={action}
                  icon="reply"
                  label={say("actions.reply")}
                  testID={`${testID}-reply`}
                  compact={floats}
                  onPress={onReply}
                />
              );
            }
            if (action === "snooze") {
              if (snoozed) {
                return (
                  <Action
                    key={action}
                    icon="alarm-clock-off"
                    label={say("actions.unsnooze")}
                    active
                    testID={`${testID}-snooze`}
                    compact={floats}
                    onPress={() => perform("attention.unsnooze", { target })}
                  />
                );
              }
              return (
                <MenuAction
                  key={action}
                  items={snoozeRows}
                  testID={`${testID}-snooze`}
                  icon="alarm-clock"
                  label={say("actions.snooze")}
                  compact={floats}
                />
              );
            }
            if (action === "pin") {
              return (
                <Action
                  key={action}
                  icon={pinScope ? "pin-off" : "pin"}
                  label={say(pinScope ? "actions.unpin" : "actions.pin")}
                  active={pinScope !== null}
                  disabled={pinScope === "all" && !capabilities.collective}
                  testID={`${testID}-pin`}
                  compact={floats}
                  onPress={() =>
                    perform(pinScope ? "attention.unpin" : "attention.pin", {
                      target,
                      ...(pinScope === "all" ? { scope: "all" } : {}),
                    })
                  }
                />
              );
            }
            return (
              <Action
                key={action}
                icon={archiveScope ? "archive-restore" : "archive"}
                label={say(archiveScope ? "actions.unarchive" : "actions.archive")}
                active={archiveScope !== null}
                disabled={archiveScope === "all" && !capabilities.collective}
                testID={`${testID}-archive`}
                compact={floats}
                onPress={() =>
                  perform(archiveScope ? "attention.unarchive" : "attention.archive", {
                    target,
                    ...(archiveScope === "all" ? { scope: "all" } : {}),
                  })
                }
              />
            );
          })}
      </Col>
      {failure ? (
        <Text typeName="caption" color="danger" testID={`${testID}-error`}>
          {failure}
        </Text>
      ) : null}
    </Col>
  );
}
